import { useState } from "react";

interface Props {
  user: {
    name?: string;
    email?: string;
    userType?: string;
    phone?: string;
  };
}

export default function ProfileSettings({ user }: Props) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [saved, setSaved] = useState(false);
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    newListings: true,
  });

  const handleSave = () => {
    // Simüle edilmiş kaydetme
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const toggleNotification = (key: keyof typeof notifications) => {
    setNotifications((p) => ({ ...p, [key]: !p[key] }));
  };

  return (
    <div className="space-y-5 md:space-y-6">
      {/* Profile Card */}
      <div className="bg-white rounded-xl border border-surface-200 p-5 md:p-6">
        <h2 className="text-sm font-bold text-surface-900 mb-5 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
            <i className="ri-user-line" />
          </div>
          Profil Bilgileri
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-surface-600 mb-1.5 block">
              {user.userType === "Emlak Ofisi" ? "Firma Adı" : "Ad Soyad"}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-surface-600 mb-1.5 block">
              E-posta
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-surface-600 mb-1.5 block">
              Telefon
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-surface-400">
                +90
              </span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="5XX XXX XX XX"
                className="w-full pl-12 pr-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-surface-600 mb-1.5 block">
              Üyelik Tipi
            </label>
            <div className="px-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800">
              {user.userType || "Bireysel"}
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors"
          >
            {saved ? (
              <span className="flex items-center gap-2">
                <i className="ri-check-line" />
                Kaydedildi
              </span>
            ) : (
              "Değişiklikleri Kaydet"
            )}
          </button>
          {saved && (
            <span className="text-xs text-emerald-600 font-medium">
              Bilgileriniz güncellendi
            </span>
          )}
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-white rounded-xl border border-surface-200 p-5 md:p-6">
        <h2 className="text-sm font-bold text-surface-900 mb-5 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
            <i className="ri-notification-3-line" />
          </div>
          Bildirim Tercihleri
        </h2>

        <div className="space-y-4">
          {[
            {
              key: "email" as const,
              label: "E-posta Bildirimleri",
              desc: "Yeni mesaj ve ilan durumu değişikliklerinde e-posta al",
            },
            {
              key: "sms" as const,
              label: "SMS Bildirimleri",
              desc: "Önemli güncellemelerde SMS al",
            },
            {
              key: "newListings" as const,
              label: "Yeni İlan Bildirimleri",
              desc: "Benzer arsalardan yeni ilan eklendiğinde haber ver",
            },
          ].map((item) => (
            <div
              key={item.key}
              className="flex items-start justify-between gap-4"
            >
              <div>
                <p className="text-sm font-medium text-surface-800">
                  {item.label}
                </p>
                <p className="text-xs text-surface-500 mt-0.5">{item.desc}</p>
              </div>
              <button
                type="button"
                onClick={() => toggleNotification(item.key)}
                className={`w-11 h-6 rounded-full relative transition-colors shrink-0 mt-0.5 ${
                  notifications[item.key] ? "bg-primary-500" : "bg-surface-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    notifications[item.key] ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-white rounded-xl border border-red-100 p-5 md:p-6">
        <h2 className="text-sm font-bold text-red-600 mb-2 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500">
            <i className="ri-alert-line" />
          </div>
          Tehlikeli Bölge
        </h2>
        <p className="text-xs text-surface-500 mb-4">
          Hesabınızı kalıcı olarak silebilirsiniz. Bu işlem geri alınamaz.
        </p>
        <button
          type="button"
          className="px-5 py-2.5 border border-red-200 text-red-600 text-sm font-semibold rounded-xl hover:bg-red-50 transition-colors"
        >
          Hesabımı Sil
        </button>
      </div>
    </div>
  );
}
